import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GoogleLogo, Envelope, Lock, User, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = [
  { id: "student", label: "Élève" },
  { id: "parent", label: "Parent" },
  { id: "teacher", label: "Enseignant" },
  { id: "school", label: "Établissement" },
];

export default function Auth() {
  const [params] = useSearchParams();
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate(user.role === "student" && !user.exam_id ? "/bienvenue" : "/app", { replace: true });
  }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Bienvenue ${u.name} !`);
        navigate(u.role === "student" && !u.exam_id ? "/bienvenue" : "/app");
      } else {
        const u = await register(form);
        toast.success("Compte créé avec succès !");
        navigate(u.role === "student" ? "/bienvenue" : "/app");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-forest p-12 text-white lg:flex">
        <div className="absolute -right-20 top-20 h-72 w-72 rounded-full bg-terracotta/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
        <Logo light />
        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold leading-tight">Réussir aujourd'hui.<br /> Construire demain.</h2>
          <p className="mt-4 max-w-md text-white/75">La plateforme de référence pour préparer les examens officiels du Cameroun avec une IA pédagogique.</p>
          <ul className="mt-8 space-y-3">
            {["Professeur IA disponible 24h/24", "Cours conformes aux programmes officiels", "Examens blancs corrigés & suivi de progression"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/85"><CheckCircle weight="fill" className="text-gold" /> {t}</li>
            ))}
          </ul>
        </div>
        <p className="relative text-sm text-white/50">© 2026 YAYA EXAM ACADEMY</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-cream px-4 py-10">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-terracotta"><ArrowLeft /> Retour à l'accueil</Link>
          <div className="lg:hidden"><Logo /></div>
          <h1 className="mt-6 font-display text-3xl font-extrabold">{mode === "login" ? "Bon retour !" : "Créer un compte"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{mode === "login" ? "Connectez-vous pour continuer votre préparation." : "Quelques secondes pour commencer à réviser."}</p>

          <button data-testid="google-login-btn" onClick={googleLogin} className="mt-7 flex w-full items-center justify-center gap-3 rounded-full border-2 border-border bg-white py-3 text-sm font-semibold transition hover:bg-sand">
            <GoogleLogo weight="bold" size={20} className="text-terracotta" /> Continuer avec Google
          </button>
          <p data-testid="google-webview-note" className="mt-2 text-center text-xs text-muted-foreground">
            La connexion Google échoue ? Ouvrez le site dans <span className="font-semibold text-ink">Safari</span> ou <span className="font-semibold text-ink">Chrome</span> (Google bloque les navigateurs intégrés).
          </p>

          <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> OU <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input id="name" data-testid="auth-name" value={form.name} onChange={set("name")} required placeholder="Aïcha Mballa" className="rounded-xl pl-10" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input id="email" data-testid="auth-email" type="email" value={form.email} onChange={set("email")} required placeholder="vous@exemple.cm" className="rounded-xl pl-10" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input id="password" data-testid="auth-password" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="rounded-xl pl-10" />
              </div>
            </div>
            {mode === "register" && (
              <div>
                <Label>Je suis</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {ROLES.map((r) => (
                    <button type="button" key={r.id} data-testid={`role-${r.id}`} onClick={() => setForm({ ...form, role: r.id })}
                      className={`rounded-xl border-2 py-2.5 text-sm font-semibold transition ${form.role === r.id ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-border text-ink/70 hover:bg-sand"}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p data-testid="auth-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

            <Button type="submit" data-testid="auth-submit" disabled={loading} className="w-full rounded-full bg-terracotta py-6 text-sm font-semibold text-white hover:bg-[#E03E00]">
              {loading ? "Veuillez patienter…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button data-testid="toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="font-semibold text-terracotta hover:underline">
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
